import {useContext, useEffect, useRef, useState} from "react";
import {ToastContext} from "Toast";
import {useMessages} from "i18n";
import {paymentAdmission} from "pay/withdrawalRight";
import {useEmbeddedSubscriptionCheckout} from "pay/useEmbeddedSubscriptionCheckout";
import type {CheckoutDocumentRead} from "pay/checkoutDocument";
import {usePricingUpgrade} from "pricing/usePricingUpgrade";
import {couponCountdown} from "./couponCountdown";
import {currentCouponToken, type CurrentLadderGift} from "./currentLadderOffer";
import {couponEventDetails} from "./couponEvents";
import {offerEvents} from "upsell/offerEvents";

/**
 * Reuses the plan-change flow without mounting its separate preview dialog.
 *
 * A NEW subscription pays IN THE DIALOG since the owner's 2026-09-09 request: `subscribe` mints an
 * embedded session (carrying the ladder token, which the endpoint has always accepted) and the claim
 * dialog mounts Stripe's sheet where the plan copy stood — no popup, no navigation. The api refusing
 * the token surfaces as this hook's `expired`, exactly as the redirect path reported it.
 *
 * A LIVE SUBSCRIBER's claim is the in-place plan change itself, charged at once, so its tick is the plan
 * change's own acknowledgement (kitecorp/transferify-ui#3457): one box, held where the commit reads it, and
 * the press waits for the plan change to admit it exactly as Subscribe waits for the new-subscription tick.
 */
export function useCouponClaim(gift: CurrentLadderGift, onClose: () => void, onExpired: () => void, deadlineExpired = false) {
	const {onUpgrade, planChange} = usePricingUpgrade(null, onExpired);
	const inPage = useEmbeddedSubscriptionCheckout();
	const [acknowledged, setAcknowledged] = useState(false);
	const [refused, setExpired] = useState(false);
	const expired = refused || deadlineExpired;
	const opening = useRef(onUpgrade);
	const hadPendingChange = useRef(false);
	const close = useRef(onClose);
	close.current = onClose;
	const token = currentCouponToken(gift.offer);
	useEffect(() => {
		if (gift.isUpgrade && token !== null) opening.current(gift.targetPlan, "MONTHLY", token);
	}, [gift.isUpgrade, gift.targetPlan, token]);
	useEffect(() => {
		if (planChange.open) hadPendingChange.current = true;
		else if (hadPendingChange.current) close.current();
	}, [planChange.open]);
	// The mint's own refusal signals, folded into the same expired state the token's local absence sets: a
	// dead token, and on the Halloween ticket (kitecorp/transferify-ui#3776) the api declining its variant,
	// which means the buyer's standing or the storefront's Price changed since the ticket was issued. Either
	// way the offer is gone; nothing retries at the regular price under a print that named 30 days.
	const reportExpired = useRef(onExpired);
	reportExpired.current = onExpired;
	useEffect(() => {
		if (inPage.offerRefused || inPage.offerUnavailable) {
			setExpired(true);
			reportExpired.current();
		}
	}, [inPage.offerRefused, inPage.offerUnavailable]);
	// The coupon's checkout step (kitecorp/transferify-ui#2914): the success card's coupon step with the
	// presentation's context, as the card's other steps are. A new subscription counts it once the session
	// exists, since a code refused at the mint opened no checkout; the plan-change path counts it at the press.
	const reportCheckout = () => {
		const {context, price} = couponEventDetails(gift);
		offerEvents.coupon("CHECKOUT_OPENED", context, price);
	};
	// The plan change asks for the request on every upgrade; a claim it asks none for keeps this hook's own box.
	// The press and the dialog's button both read admission off this one tick, so they cannot disagree.
	const tick = gift.isUpgrade && planChange.acknowledgement !== null
		? planChange.acknowledgement
		: {checked: acknowledged, onChange: setAcknowledged};
	// A getter, matching usePromoClaim's half of {@link CouponClaimActions}: this path awaits no sign-in,
	// but one shared shape keeps the two from drifting into capturing versus re-reading.
	const subscribe = (readSelection?: () => CheckoutDocumentRead | undefined) => {
		if (expired) return;
		if (couponCountdown(gift.offer.expiresAt, Date.now()).expired) { onExpired(); return; }
		if (paymentAdmission(tick.checked) === "acknowledgementRequired") return;
		if (gift.isUpgrade) { reportCheckout(); planChange.onConfirm(); return; }
		if (inPage.busy) return;
		if (token === null) { setExpired(true); onExpired(); return; }
		const documentSelection = readSelection?.();
		// The ticket's variant rides beside the token (ui#3776): the token names the row, the variant shapes the Session.
		void inPage.start(gift.targetPlan, "MONTHLY", {offerToken: token, uiMode: "elements", immediatePerformanceConsent: acknowledged,
			...(gift.intro === undefined ? {} : {offerVariant: gift.intro.variant}), ...(documentSelection ? {documentSelection} : {})})
			.then(outcome => { if (outcome === "ready") reportCheckout(); });
	};
	// Paid in place: unmount the sheet, say it is being confirmed — the plan switches on when
	// `invoice.paid` lands, so no claim about the new plan yet — then close. The SAME confirming
	// sentence the first-month claim shows, because it is the same moment.
	const {banner} = useMessages();
	const {showSuccessToast} = useContext(ToastContext);
	const paid = () => {
		inPage.completed();
		showSuccessToast(banner.firstMonthOffer.dialog.checkout.confirming);
		close.current();
	};
	return {subscribe, acknowledged: tick.checked, setAcknowledged: tick.onChange, busy: inPage.busy || planChange.confirming, expired,
		previewStatus: gift.isUpgrade ? planChange.status : "ready", amountDueNow: planChange.amount,
		clientSecret: inPage.clientSecret, collectsShippingAddress: inPage.collectsShippingAddress,
		checkoutError: inPage.error, paid, dismissSheet: inPage.dismiss};
}
